import React, { useCallback, useEffect, useMemo, useState } from "react";

import Pagination from "src/components/Pagination/Pagination";
import useControlPanel from "src/hooks/useControlPanel";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import ReleaseAppVersionControlPanel from "../../components/ControlPanel/ReleaseAppVersionControlPanel";
import ReleaseAppVersionPanelOptional from "../../components/ControlPanel/PanelOptional/ReleaseAppVersionPanelOptional";
import ReleaseAppVersionTable from "../../components/Table/ReleaseAppVersionTable";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";
import {
  createReleaseAppVersion,
  getReleaseAppVersion,
  updateReleaseAppVersion,
  updateReleaseAppVersionMode,
} from "src/apis/VersionRelease/appVersion.api";
import { CACHE_RELEASE_APP_VERSION } from "src/constants/actionCache";
import { NotifyConfirm } from "src/components/notify/Notify";
import { path } from "src/constants/path";
import { MODE_RELEASE } from "../../components/Table/Helper";
import { dataStateType } from "src/constants/status";

const initReleaseAppVersionData = {
  list: [],
  total_record: 0,
};

const handleNotifyMessage = (mode, releaseAppVersion) => {
  const modeRelease =
    mode === MODE_RELEASE.releaseOnGroup ? "On Group" : "Mass";
  const title = `<h5>Release Version ${releaseAppVersion.appVersion} - Mode ${modeRelease}</h5>`;
  const text = `
    <div>
      <h5>OS Name: <span style="color: red;">${releaseAppVersion.operatingSystem?.name}</span></h5>
      <h5>Version: <span style="color: red;">${releaseAppVersion.appVersion}</span></h5>
      <h5>Mode Release: <span style="color: red;">${modeRelease}</span></h5>
      <h5 style="color: red;" > 
        Please check, before making confirmation
      </h5>
    </div>
  `;

  return { title, text };
};

const titlePage = "App Version";

const ReleaseAppVersion = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();

  // cache
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();

  const [releaseAppVersionData, setReleaseAppVersionData] = useState(
    initReleaseAppVersionData
  );
  const [filterReleaseAppVersions, setFilterReleaseAppVersions] = useState([]);
  const [releaseAppOSId, setReleaseAppOSId] = useState("all");

  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [errorsForm, setErrorsForm] = useState({});

  const fetchReleaseAppVersions = useCallback(() => {
    getReleaseAppVersion({}, (result) => {
      setReleaseAppVersionData({ ...result });
      setFilterReleaseAppVersions([...result.list]);
    });
  }, []);

  useEffect(() => {
    fetchReleaseAppVersions();
  }, [fetchReleaseAppVersions]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const updateFilteredReleaseAppVersion = useCallback((releaseAppVersions) => {
    setFilterReleaseAppVersions(releaseAppVersions);
  }, []);

  /* ---- handle search ----- */

  const returnListReleaseAppVersionByOSName = useCallback(() => {
    if (status === dataStateType.INACTIVE) {
      return [];
    }
    if (releaseAppOSId === "all") {
      return filterReleaseAppVersions;
    } else {
      return filterReleaseAppVersions.filter(
        (releaseApp) => releaseApp.operatingSystemId === releaseAppOSId
      );
    }
  }, [filterReleaseAppVersions, releaseAppOSId, status]);

  const searchByKeyWord = useCallback((releaseAppVersions, key) => {
    return releaseAppVersions.filter((releaseAppVersion) => {
      return releaseAppVersion.name
        .toLowerCase()
        .includes(key.toLowerCase().trim());
    });
  }, []);

  const handleSearch = useCallback(
    (value) => {
      const filteredReleaseAppVersions = value
        ? searchByKeyWord(releaseAppVersionData.list, value)
        : releaseAppVersionData.list;
      updateFilteredReleaseAppVersion(filteredReleaseAppVersions);
    },
    [
      releaseAppVersionData.list,
      searchByKeyWord,
      updateFilteredReleaseAppVersion,
    ]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  const handleChangeReleaseAppOS = (value) => {
    setReleaseAppOSId(value);
    setNumberPage(1);
  };

  /* ---- handle add new and update ----- */
  const addOrEdit = useCallback(
    (releaseAppVersion, resetForm) => {
      if (releaseAppVersion.id) {
        updateReleaseAppVersion(
          releaseAppVersion.id,
          releaseAppVersion,
          handleApiResponse
        );
      } else {
        createReleaseAppVersion(releaseAppVersion, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchReleaseAppVersions();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_RELEASE_APP_VERSION);
        }
      }
    },
    [fetchReleaseAppVersions, setCurrentActionCache]
  );

  /* ---- handle change mode release ----- */
  const handleChangeModeRelease = (valueRelease, mode, releaseAppVersion) => {
    const { title, text } = handleNotifyMessage(mode, releaseAppVersion);
    if (!valueRelease) {
      NotifyConfirm(
        mode === MODE_RELEASE.releaseMass ? "warning" : "info",
        text,
        (result) => {
          if (result.isConfirmed) {
            let dataUpdate = {
              operatingSystemId: releaseAppVersion.operatingSystemId,
              isReleaseOnGroup: releaseAppVersion.isReleaseOnGroup,
              isReleaseMass: releaseAppVersion.isReleaseMass,
            };

            if (
              !releaseAppVersion.isReleaseOnGroup &&
              !releaseAppVersion.isReleaseMass
            ) {
              dataUpdate = {
                ...dataUpdate,
                isReleaseOnGroup:
                  mode === MODE_RELEASE.releaseOnGroup
                    ? !valueRelease
                    : releaseAppVersion.isReleaseOnGroup,
                isReleaseMass:
                  mode === MODE_RELEASE.releaseMass
                    ? !valueRelease
                    : releaseAppVersion.isReleaseMass,
              };
            } else {
              dataUpdate = {
                ...dataUpdate,
                isReleaseOnGroup:
                  mode === MODE_RELEASE.releaseOnGroup
                    ? !valueRelease
                    : !releaseAppVersion.isReleaseOnGroup,
                isReleaseMass:
                  mode === MODE_RELEASE.releaseMass
                    ? !valueRelease
                    : !releaseAppVersion.isReleaseMass,
              };
            }
            updateReleaseAppVersionMode(
              releaseAppVersion.id,
              dataUpdate,
              (_) => {
                getReleaseAppVersion({}, (result) => {
                  setReleaseAppVersionData({ ...result });
                  setFilterReleaseAppVersions([...result.list]);
                });
                setRecordForEdit(null);
                setCurrentActionCache(CACHE_RELEASE_APP_VERSION);
              }
            );
          }
        },
        title
      );
    }
  };

  /* ---- result for render ----- */
  const resultFilter = useMemo(() => {
    const appVersionFilter = returnListReleaseAppVersionByOSName();
    return searchByKeyWord(appVersionFilter, searchKey);
  }, [returnListReleaseAppVersionByOSName, searchByKeyWord, searchKey]);

  const listItem = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>{titlePage}</h2>
      </div>

      <ReleaseAppVersionControlPanel
        handleOpenPopup={handleOpenPopup}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={true}
        />
        <ReleaseAppVersionPanelOptional
          onChange={handleChangeReleaseAppOS}
          releaseAppOSId={releaseAppOSId}
        />
      </ReleaseAppVersionControlPanel>

      <ReleaseAppVersionTable
        handleOpenPopup={handleOpenPopup}
        handleChangeModeRelease={handleChangeModeRelease}
        data={listItem}
        status={status}
      />

      <Pagination
        pathname={path.releaseAppVersion}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default ReleaseAppVersion;
